import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectTheme } from "../../../Store/features/design/designSlice";
import { FaTwitter, FaLinkedinIn, FaInstagram, FaGlobe, FaPlus } from "react-icons/fa";
import { getSearchResultsAPI, getSpotlightProducts } from "../../../Service/user";
import "./CreateProductPopup.scss";
import { toast } from "react-hot-toast";

const CreateProductPopup = ({ isOpen, onClose, onSubmit, initialData, isEditing }) => {
	const theme = useSelector(selectTheme);
	const user = useSelector((state) => state.user.loggedInUser);
	const [step, setStep] = useState(isEditing ? 2 : 1);
	const [productLink, setProductLink] = useState(initialData?.productLink || "");
	const [searchInput, setSearchInput] = useState("");
	const [searchResults, setSearchResults] = useState([]);
	const [searchLoading, setSearchLoading] = useState(false);
	const [existingProducts, setExistingProducts] = useState([]);
	const [selectedExistingProduct, setSelectedExistingProduct] = useState(null);
	const [isResubmission, setIsResubmission] = useState(false);
	const [formData, setFormData] = useState({
		// Step 1: Product Link - handled separately
		
		// Step 2: Main Info
		title: initialData?.title || "",
		tagline: initialData?.tagline || "",
		socialLinks: initialData?.socialLinks || {
			website: "",
			twitter: "",
			instagram: "",
			linkedin: "",
		},
		aboutCompany: initialData?.aboutCompany || "",
		coreValueProposition: initialData?.coreValueProposition || [""],
		tags: initialData?.tags || [""],
		industry: initialData?.industry || "",
		firstComment: initialData?.firstComment || "",
		
		// Step 3: Media
		logo: initialData?.logo || "",
		banner: initialData?.banner || "",
		productImages: initialData?.productImages || [""],
		
		// Step 4: Makers
		makers: initialData?.makers || [],
		
		// Step 5: Extras
		pricing: initialData?.pricing || "Free",
		priceAmount: initialData?.priceAmount || "",
		priceType: initialData?.priceType || "One-time",
		promoCode: initialData?.promoCode || "",
		stage: initialData?.stage || "Idea",
		kycDocuments: {
			aadharCard: "",
			panCard: ""
		},
		pitchDeck: {
			document: "",
			requestedFromAnalyst: false
		},
		termsAccepted: false
	});

	// Refs for file inputs
	const logoInputRef = useRef(null);
	const bannerInputRef = useRef(null);
	const productImagesInputRefs = useRef([]);

	// Initialize refs for product images
	useEffect(() => {
		// Initialize the refs for product images
		productImagesInputRefs.current = Array(formData.productImages.length).fill().map(() => null);
	}, [formData.productImages.length]);

	// Fetch user's existing products on component mount
	useEffect(() => {
		const fetchExistingProducts = async () => {
			try {
				const response = await getSpotlightProducts();
				if (response.status === 200) {
					const userProducts = response.products.filter(product => 
						product.founder === JSON.parse(localStorage.getItem("loggedInUser"))._id
					);
					setExistingProducts(userProducts);
				}
			} catch (error) {
				console.error("Error fetching existing products:", error);
			}
		};

		if (isOpen) {
			fetchExistingProducts();
		}
	}, [isOpen]);

	// Add a more comprehensive useEffect for initializing data in edit mode
	useEffect(() => {
		if (isEditing && initialData) {
			
			// Set product link
			setProductLink(initialData.productLink || initialData?.socialLinks?.website || "");
			
			// Set form data with all fields from initialData
			setFormData({
				// Step 2: Main Info
				title: initialData.title || "",
				tagline: initialData.tagline || "",
				socialLinks: initialData.socialLinks || {
					website: "",
					twitter: "",
					instagram: "",
					linkedin: "",
				},
				aboutCompany: initialData.aboutCompany || "",
				coreValueProposition: Array.isArray(initialData.coreValueProposition) && initialData.coreValueProposition.length > 0 
					? initialData.coreValueProposition 
					: [""],
				tags: Array.isArray(initialData.tags) && initialData.tags.length > 0 
					? initialData.tags 
					: [""],
				industry: initialData.industry || "",
				firstComment: typeof initialData.firstComment === 'string' 
					? initialData.firstComment 
					: "",
				
				// Step 3: Media
				logo: initialData.logo || "",
				banner: initialData.banner || "",
				productImages: Array.isArray(initialData.productImages) && initialData.productImages.length > 0 
					? initialData.productImages 
					: [""],
				
				// Step 4: Makers
				makers: Array.isArray(initialData.makers) && initialData.makers.length > 0 
					? initialData.makers.map(maker => ({
						_id: maker._id,
						firstName: maker.firstName,
						lastName: maker.lastName,
						profilePicture: maker.profilePicture,
						userName: maker.userName
					}))
					: [],
				
				// Step 5: Extras
				pricing: initialData.pricing || "Free",
				priceAmount: initialData.priceAmount || "",
				priceType: initialData.priceType || "One-time",
				promoCode: initialData.promoCode || "",
				stage: initialData.stage || "Idea",
				kycDocuments: initialData.kycDocuments || {
					aadharCard: "",
					panCard: ""
				},
				pitchDeck: initialData.pitchDeck || {
					document: "",
					requestedFromAnalyst: false
				},
				termsAccepted: initialData.termsAccepted || false
			});
			
		}
	}, [isEditing, initialData]);

	// Handle selecting existing product
	const handleExistingProductSelect = (product) => {
		setSelectedExistingProduct(product);
		setFormData({
			...formData,
			title: product.title,
			tagline: product.tagline,
			socialLinks: product.socialLinks,
			aboutCompany: product.aboutCompany,
			coreValueProposition: product.coreValueProposition,
			tags: product.tags,
			industry: product.industry,
			logo: product.logo,
			banner: product.banner,
			productImages: product.productImages,
			makers: product.makers,
			pricing: product.pricing,
			priceAmount: product.priceAmount,
			priceType: product.priceType,
			promoCode: product.promoCode,
			stage: product.stage,
			kycDocuments: product.kycDocuments || {
				aadharCard: "",
				panCard: ""
			},
			pitchDeck: product.pitchDeck || {
				document: "",
				requestedFromAnalyst: false
			},
			termsAccepted: product.termsAccepted || false
		});
		setProductLink(product.productLink);
	};

	// Basic form field change handler
	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});
		
		// Only try to remove error class if e.target is a DOM element
		if (e.target instanceof Element && e.target.classList.contains('input-error')) {
			e.target.classList.remove('input-error');
		}
	};

	// Handle social links change
	const handleSocialLinkChange = (e) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			socialLinks: {
				...formData.socialLinks,
				[name]: value,
			},
		});
	};

	// Handle tag changes
	const handleTagChange = (index, value) => {
		const updatedTags = [...formData.tags];
		updatedTags[index] = value;
		setFormData({
			...formData,
			tags: updatedTags,
		});
	};

	const addTag = () => {
		setFormData({
			...formData,
			tags: [...formData.tags, ""],
		});
	};

	const removeTag = (index) => {
		const updatedTags = [...formData.tags];
		updatedTags.splice(index, 1);
		setFormData({
			...formData,
			tags: updatedTags,
		});
	};

	// Handle core value proposition changes
	const handleCoreValueChange = (index, value) => {
		const updatedCVP = [...formData.coreValueProposition];
		updatedCVP[index] = value;
		setFormData({
			...formData,
			coreValueProposition: updatedCVP,
		});
	};

	const addCoreValue = () => {
		setFormData({
			...formData,
			coreValueProposition: [...formData.coreValueProposition, ""],
		});
	};

	const removeCoreValue = (index) => {
		const updatedCVP = [...formData.coreValueProposition];
		updatedCVP.splice(index, 1);
		setFormData({
			...formData,
			coreValueProposition: updatedCVP,
		});
	};

	// Handle productImages image changes
	const handleproductImagesChange = (index, value) => {
		const updatedproductImages = [...formData.productImages];
		updatedproductImages[index] = value;
		setFormData({
			...formData,
			productImages: updatedproductImages,
		});
	};

	const addproductImagesImage = () => {
		setFormData({
			...formData,
			productImages: [...formData.productImages, ""],
		});
		
		// Need to update the references in the next render
		setTimeout(() => {
			productImagesInputRefs.current = productImagesInputRefs.current.slice(0, formData.productImages.length + 1);
		}, 0);
	};

	const removeproductImagesImage = (index) => {
		const updatedproductImages = [...formData.productImages];
		updatedproductImages.splice(index, 1);
		setFormData({
			...formData,
			productImages: updatedproductImages,
		});
		
		// Need to update the references in the next render
		setTimeout(() => {
			productImagesInputRefs.current = productImagesInputRefs.current.slice(0, updatedproductImages.length);
		}, 0);
	};

	// File conversion utilities
	const convertToBase64 = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => reject(error);
		});
	};

	const handleFileChange = async (e, field) => {
		if (e.target.files && e.target.files[0]) {
			try {
				const base64String = await convertToBase64(e.target.files[0]);
				setFormData({
					...formData,
					[field]: base64String,
				});
			} catch (error) {
				console.error("Error converting file to base64:", error);
			}
		}
	};

	const handleproductImagesFileChange = async (e, index) => {
		if (e.target.files && e.target.files[0]) {
			try {
				const base64String = await convertToBase64(e.target.files[0]);
				handleproductImagesChange(index, base64String);
			} catch (error) {
				console.error("Error converting file to base64:", error);
			}
		}
	};

	// Helper function to display file name from base64
	const getFileNameFromBase64 = (base64String) => {
		if (!base64String) return "";
		const fileType = base64String.split(";")[0].split("/")[1];
		return `image.${fileType}`;
	};

	// Search for makers
	const searchMakers = async (query) => {
		if (!query.trim()) {
			setSearchResults([]);
			return;
		}
		
		try {
			setSearchLoading(true);
			const { data } = await getSearchResultsAPI(query);
			setSearchResults(data?.users || []);
		} catch (error) {
			console.error("Error getting search results:", error);
			setSearchResults([]);
		} finally {
			setSearchLoading(false);
		}
	};

	const handleSearchInputChange = (e) => {
		setSearchInput(e.target.value);
	};

	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			searchMakers(searchInput);
		}, 300);

		return () => clearTimeout(delayDebounceFn);
	}, [searchInput]);

	// Maker functions
	const addMaker = (user) => {
		if (formData.makers.find(maker => maker._id === user._id)) return;
		
		setFormData({
			...formData,
			makers: [...formData.makers, user]
		});
		setSearchInput("");
	};

	const removeMaker = (userId) => {
		setFormData({
			...formData,
			makers: formData.makers.filter(maker => maker._id !== userId)
		});
	};

	// Navigation between steps
	const goToNextStep = () => {
		// Validate terms and conditions first
		if (step === 1) {
			if (!formData.termsAccepted) {
				toast.error("Please accept the terms and conditions to proceed");
				return;
			}
			setStep(step + 1);
			return;
		}

		// Validate first step - Product Link
		if (step === 2) {
			if (!productLink.trim()) {
				// Show error if link is empty
				document.getElementById('product-link-input').classList.add('input-error');
				return;
			}
			
			// Basic URL validation
			let url = productLink.trim();
			if (!/^https?:\/\//i.test(url)) {
				url = 'https://' + url;
				setProductLink(url);
			}
			
			try {
				new URL(url);
				// URL is valid, proceed to next step
				setStep(step + 1);
			} catch (error) {
				// URL is invalid
				document.getElementById('product-link-input').classList.add('input-error');
				return;
			}
		} else if (step === 3) {
			// Validate step 2 mandatory fields
			if (!formData.title.trim()) {
				document.querySelector('input[name="title"]').classList.add('input-error');
				return;
			}
			if (!formData.tagline.trim()) {
				document.querySelector('input[name="tagline"]').classList.add('input-error');
				return;
			}
			if (!formData.aboutCompany.trim()) {
				document.querySelector('textarea[name="aboutCompany"]').classList.add('input-error');
				return;
			}
			if (!formData.coreValueProposition[0].trim()) {
				document.querySelector('.cvp-input input').classList.add('input-error');
				return;
			}
			// All validations passed, proceed to next step
			setStep(step + 1);
		} else if (step === 4) {
			// Validate step 3 mandatory fields - require at least logo and one product image
			if (!formData.logo) {
				toast.error("Please upload a logo for your product");
				return;
			}
			if (!formData.productImages[0] || formData.productImages[0] === "") {
				toast.error("Please upload at least one product image");
				return;
			}
			setStep(step + 1);
		} else if (step === 5) {
			// In editing mode, skip step 6
			if (isEditing) {
				handleSubmit({ preventDefault: () => {} });
				return;
			}
			// Proceed to next step
			setStep(step + 1);
		} else if (step === 6) {
			// In editing mode, skip step 7
			if (isEditing) {
				handleSubmit({ preventDefault: () => {} });
				return;
			}
			// Proceed to next step
			setStep(step + 1);
		} else {
			// Other steps don't need validation for now
			setStep(step + 1);
		}
	};

	const handleLinkChange = (e) => {
		setProductLink(e.target.value);
		// Remove error class when user starts typing
		if (document.getElementById('product-link-input').classList.contains('input-error')) {
			document.getElementById('product-link-input').classList.remove('input-error');
		}
	};

	const goToPrevStep = () => {
		setStep(step - 1);
	};

	// Add a new handler specifically for stage selection
	const handleStageSelect = (stage) => {
		setFormData({
			...formData,
			stage: stage
		});
	};

	const [formErrors, setFormErrors] = useState({});

	const termsAndConditions = `By submitting your product to CapitalHub, you agree to the following terms and conditions:

1. Content Ownership: You confirm that you own or have the necessary rights to all content submitted, including images, text, and other materials.

2. Accuracy of Information: You warrant that all information provided about your product is accurate, truthful, and not misleading.

3. Intellectual Property: You retain ownership of your intellectual property, but grant CapitalHub a non-exclusive license to display and promote your product on the platform.

4. Community Guidelines: You agree to comply with CapitalHub's community guidelines and maintain professional conduct in all interactions.

5. Data Privacy: You acknowledge that your product information will be publicly visible and agree to the processing of your data in accordance with our privacy policy.

6. Updates and Modifications: You agree to keep your product information up to date and notify CapitalHub of any significant changes.

7. Termination: CapitalHub reserves the right to remove your product listing if it violates our terms or community guidelines.

8. Liability: You agree to indemnify CapitalHub against any claims arising from your product submission or content.

9. Changes to Terms: CapitalHub may modify these terms at any time, and continued use of the platform constitutes acceptance of modified terms.

10. Governing Law: These terms are governed by the laws of India, and any disputes shall be subject to the exclusive jurisdiction of the courts in Mumbai.`;

	const validateForm = () => {
		const errors = {};
		
		// For Early and Growth stages
		if ((formData.stage === "Early" || formData.stage === "Growth")) {
			// KYC validation - only if user doesn't have verified KYC
			if (!user?.kycDocuments?.aadharCard && !user?.kycDocuments?.panCard) {
				if (!formData.kycDocuments.aadharCard) {
					errors.aadharCard = "Aadhar Card is required";
				}
				if (!formData.kycDocuments.panCard) {
					errors.panCard = "PAN Card is required";
				}
			}
			
			// Pitch deck validation - either upload or request must be selected
			if (!formData.pitchDeck.document && !formData.pitchDeck.requestedFromAnalyst) {
				errors.pitchDeck = "Please either upload a pitch deck or request a review from analyst";
			}
		}

		// Terms and conditions validation
		if (!formData.termsAccepted) {
			errors.terms = "You must accept the terms and conditions to proceed";
		}
		
		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	// Form submission
	const handleSubmit = (e) => {
		e.preventDefault();
		
		if (!validateForm()) {
			toast.error("Please fill all mandatory fields");
			return;
		}
		
		if (isEditing) {
			// For editing, only include fields that have changed
			const originalData = initialData || {};
			const changedData = {};

			// Track which fields have changed
			Object.keys(formData).forEach(key => {
				// Skip firstComment entirely when editing
				if (key === 'firstComment') {
					return;
				}
                
				// Special handling for arrays and objects - deep comparison
				if (Array.isArray(formData[key])) {
					// Skip empty arrays in the comparison
					const formDataArray = formData[key].filter(item => 
						item !== "" && item !== null && item !== undefined
					);
					const originalDataArray = Array.isArray(originalData[key]) 
						? originalData[key].filter(item => 
							item !== "" && item !== null && item !== undefined
						) 
						: [];
					
					if (JSON.stringify(formDataArray) !== JSON.stringify(originalDataArray)) {
						changedData[key] = formDataArray.length > 0 ? formDataArray : [""];
					}
				} else if (typeof formData[key] === 'object' && formData[key] !== null) {
					if (JSON.stringify(formData[key]) !== JSON.stringify(originalData[key] || {})) {
						changedData[key] = formData[key];
					}
				} else if (formData[key] !== originalData[key]) {
					changedData[key] = formData[key];
				}
			});

			// Always include critical fields required by the backend
			changedData.productLink = initialData.productLink || initialData?.socialLinks?.website || "";
			
			// Ensure industry is included
			if (!changedData.industry) {
				changedData.industry = initialData.industry || formData.industry;
			}
			
			// Ensure title is included
			if (!changedData.title) {
				changedData.title = initialData.title || formData.title;
			}
			
			// Ensure socialLinks is properly structured
			if (!changedData.socialLinks && formData.socialLinks) {
				changedData.socialLinks = {
					...initialData.socialLinks || {},
					...formData.socialLinks
				};
			}
			
			// Always preserve the original firstComment
			if (initialData.firstComment) {
				changedData.firstComment = initialData.firstComment;
			}

			// Submit only the changed data
			onSubmit(changedData);
		} else {
			// For new submission, include all fields
			const completeFormData = {
				...formData,
				socialLinks: {
					...formData.socialLinks,
					website: productLink
				},
				productLink: productLink
			};
			
			// Only add firstComment if it's not empty
			if (formData.firstComment && formData.firstComment.trim()) {
				completeFormData.firstComment = formData.firstComment;
			}

			// Add existingProductId if this is a resubmission
			if (isResubmission && selectedExistingProduct) {
				completeFormData.existingProductId = selectedExistingProduct._id;
			}
			
			onSubmit(completeFormData);
		}
		
		onClose();
		// Reset form state
		setStep(1);
		setProductLink("");
		setIsResubmission(false);
		setSelectedExistingProduct(null);
		setFormData({
			title: "",
			tagline: "",
			socialLinks: {
				website: "",
				twitter: "",
				instagram: "",
				linkedin: "",
			},
			aboutCompany: "",
			coreValueProposition: [""],
			tags: [""],
			firstComment: "",
			logo: "",
			banner: "",
			productImages: [""],
			makers: [],
			pricing: "Free",
			priceAmount: "",
			priceType: "One-time",
			promoCode: "",
			stage: "Idea",
			kycDocuments: {
				aadharCard: "",
				panCard: ""
			},
			pitchDeck: {
				document: "",
				requestedFromAnalyst: false
			},
			termsAccepted: false
		});
	};

	// Add these new handler functions before the return statement
	const handleKYCFileChange = async (e, documentType) => {
		if (e.target.files && e.target.files[0]) {
			try {
				const file = e.target.files[0];
				const base64 = await convertToBase64(file);
				setFormData({
					...formData,
					kycDocuments: {
						...formData.kycDocuments,
						[documentType]: base64
					}
				});
			} catch (error) {
				console.error("Error converting file:", error);
			}
		}
	};

	const handlePitchDeckFileChange = async (e) => {
		if (e.target.files && e.target.files[0]) {
			try {
				const file = e.target.files[0];
				const base64 = await convertToBase64(file);
				setFormData({
					...formData,
					pitchDeck: {
						...formData.pitchDeck,
						document: base64
					}
				});
			} catch (error) {
				console.error("Error converting file:", error);
			}
		}
	};

	const handlePitchDeckRequestChange = (e) => {
		setFormData({
			...formData,
			pitchDeck: {
				...formData.pitchDeck,
				requestedFromAnalyst: e.target.checked
			}
		});
	};

	if (!isOpen) return null;

	const industryOptions = [
		"AI/ML",
		"Agritech",
		"Consumer",
		"Digital Entertainment",
		"Edtech",
		"Fintech",
		"HealthTech",
		"Media",
		"Mobility",
		"SaaS",
		"IOT"
	];

	return (
		<div className="ssp-popup-overlay">
			<div 
				className={`ssp-create-product-popup ${theme === "dark" ? "dark-theme" : ""}`} 
				style={{ 
					backgroundColor: theme === "dark" ? "#1A1A1A" : "#fff",
					color: theme === "dark" ? "#f0f0f0" : "inherit",
					boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)"
				}}
			>
				<div className="ssp-popup-header" style={{
					backgroundColor: theme === "dark" ? "#222" : "#fff",
					borderBottom: theme === "dark" ? "1px solid #333" : "1px solid #eaeaea"
				}}>
					<h2>Submit a product</h2>
					<button className="close-button" onClick={onClose}>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<line x1="18" y1="6" x2="6" y2="18"></line>
							<line x1="6" y1="6" x2="18" y2="18"></line>
						</svg>
					</button>
				</div>

				<div className="ssp-popup-content">
					{/* Progress indicator - Update to include Step 7 */}
					<div className="progress-steps">
						{!isEditing && <div className={`step ${step >= 1 ? "active" : ""}`}>1</div>}
						{!isEditing && <div className={`step-line ${step >= 2 ? "active" : ""}`}></div>}
						<div className={`step ${step >= 2 ? "active" : ""}`}>{isEditing ? "1" : "2"}</div>
						<div className={`step-line ${step >= 3 ? "active" : ""}`}></div>
						<div className={`step ${step >= 3 ? "active" : ""}`}>{isEditing ? "2" : "3"}</div>
						<div className={`step-line ${step >= 4 ? "active" : ""}`}></div>
						<div className={`step ${step >= 4 ? "active" : ""}`}>{isEditing ? "3" : "4"}</div>
						{!isEditing && <div className={`step-line ${step >= 5 ? "active" : ""}`}></div>}
						{!isEditing && <div className={`step ${step >= 5 ? "active" : ""}`}>5</div>}
						{!isEditing && <div className={`step-line ${step >= 6 ? "active" : ""}`}></div>}
						{!isEditing && <div className={`step ${step >= 6 ? "active" : ""}`}>6</div>}
						{!isEditing && <div className={`step-line ${step >= 7 ? "active" : ""}`}></div>}
						{!isEditing && <div className={`step ${step >= 7 ? "active" : ""}`}>7</div>}
					</div>

					<form onSubmit={handleSubmit}>
						{/* Step 1: Terms and Conditions - Only show in non-editing mode */}
						{step === 1 && !isEditing && (
							<div className="form-step">
								<h3>Terms and Conditions</h3>
								<p className="helper-text">Please review and accept the terms and conditions to submit your product.</p>
								
								<div className="terms-and-conditions-section">
									<div className="terms-content">
										{termsAndConditions.split('\n\n').map((paragraph, index) => (
											<p key={index}>{paragraph}</p>
										))}
									</div>
									<div className="terms-checkbox">
										<label>
											<input
												type="checkbox"
												checked={formData.termsAccepted}
												onChange={(e) => setFormData({
													...formData,
													termsAccepted: e.target.checked
												})}
												required
											/>
											I have read and agree to the terms and conditions
										</label>
									</div>
								</div>
								
								<div className="form-actions">
									<button type="button" className="next-button" onClick={goToNextStep}>
										Next
									</button>
								</div>
							</div>
						)}

						{/* Step 2: Product Link - Only show in non-editing mode */}
						{step === 2 && !isEditing && (
							<div className="form-step">
								<div className="intro-section">
									<h3>Found a cool product you want everyone to know about?</h3>
									<p>Or maybe you made one yourself and want the world to know about it? You're in the right place. So relax and follow the steps.</p>
								</div>

								<div className="submission-type-selector">
									<button
										type="button"
										className={`submission-type-btn ${!isResubmission ? 'active' : ''}`}
										onClick={() => {
											setIsResubmission(false);
											setSelectedExistingProduct(null);
											setFormData({
												...formData,
												title: "",
												productLink: "",
												industry: ""
											});
										}}
									>
										Submit New Product
									</button>
									<button
										type="button"
										className={`submission-type-btn ${isResubmission ? 'active' : ''}`}
										onClick={() => setIsResubmission(true)}
									>
										Resubmit Existing Product
									</button>
								</div>

								{isResubmission ? (
									<div className="form-group">
										<label>Select Existing Product</label>
										<div className="existing-products-list">
											{existingProducts.length > 0 ? (
												existingProducts.map(product => (
													<div 
														key={product._id} 
														className={`existing-product-item ${selectedExistingProduct?._id === product._id ? 'selected' : ''}`}
														onClick={() => handleExistingProductSelect(product)}
													>
														<img src={product.logo} alt={product.title} className="product-logo" />
														<div className="product-info">
															<h4>{product.title}</h4>
															<p>{product.tagline}</p>
														</div>
													</div>
												))
											) : (
												<p className="no-products-message">No existing products found</p>
											)}
										</div>
									</div>
								) : (
									<>
										<div className="form-group">
											<label>Name of the product</label>
											<input
												type="text"
												name="title"
												value={formData.title}
												onChange={handleChange}
												placeholder="Enter product name"
												required
											/>
										</div>

										<div className="form-group">
											<label>Link to the product</label>
											<input
												id="product-link-input"
												type="url"
												value={productLink}
												onChange={handleLinkChange}
												placeholder="Enter the product's website URL"
												required
											/>
											<div className="input-error-message">Please enter a valid URL</div>
										</div>

										<div className="form-group">
											<label>Industry</label>
											<select
												name="industry"
												value={formData.industry}
												onChange={handleChange}
												required
											>
												<option value="" disabled>Select an industry</option>
												{industryOptions.map((industry) => (
													<option key={industry} value={industry}>
														{industry}
													</option>
												))}
											</select>
										</div>
									</>
								)}
								
								<div className="form-actions">
									<button 
										type="button" 
										className="next-button"
										onClick={goToNextStep}
										disabled={isResubmission && !selectedExistingProduct}
									>
										Get started
									</button>
								</div>
							</div>
						)}

						{/* Step 2: Main Info */}
						{step === 3 && (
							<div className="form-step">
								<h3>Main Information</h3>
								
								<div className="form-group">
									<label>Tagline</label>
									<input
										type="text"
										name="tagline"
										value={formData.tagline}
										onChange={handleChange}
										placeholder="A short, catchy tagline for your product"
										required
									/>
								</div>

								{isEditing && (
									<div className="form-group">
										<label>Industry</label>
										<select
											name="industry"
											value={formData.industry}
											onChange={handleChange}
											required
										>
											<option value="" disabled>Select an industry</option>
											{industryOptions.map((industry) => (
												<option key={industry} value={industry}>
													{industry}
												</option>
											))}
										</select>
									</div>
								)}

								<div className="form-group">
									<label>About the Company</label>
									<textarea
										name="aboutCompany"
										value={formData.aboutCompany}
										onChange={handleChange}
										placeholder="Tell us about your company"
										rows="4"
										required
									></textarea>
								</div>

								<div className="form-group">
									<label>Core Value Proposition</label>
									<div className="tags-container cvp-container">
										{formData.coreValueProposition.map((value, index) => (
											<div className="tag-input cvp-input" key={`cvp-${index}`}>
												<input
													type="text"
													value={value}
													onChange={(e) => handleCoreValueChange(index, e.target.value)}
													placeholder="Enter value proposition (1-2 lines)"
													maxLength={200}
													className="full-width"
												/>
												<button
													type="button"
													className="remove-button"
													onClick={() => removeCoreValue(index)}
													disabled={formData.coreValueProposition.length === 1}
												>
													<svg
														xmlns="http://www.w3.org/2000/svg"
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													>
														<line x1="18" y1="6" x2="6" y2="18"></line>
														<line x1="6" y1="6" x2="18" y2="18"></line>
													</svg>
												</button>
											</div>
										))}
										<button type="button" className="add-button" onClick={addCoreValue}>
											Add Value Proposition
										</button>
									</div>
								</div>

								<div className="form-group">
									<label>Social Links</label>
									<div className="social-links-grid">
										<div className="social-link-input">
											<span className="social-icon">
												<FaGlobe />
											</span>
											<input
												type="url"
												disabled
												value={productLink}
												placeholder={`${isEditing ? "Website URL set by User" : "Website URL (from Step 1)"}`}
											/>
										</div>
										<div className="social-link-input">
											<span className="social-icon">
												<FaTwitter />
											</span>
											<input
												type="url"
												name="twitter"
												value={formData.socialLinks.twitter}
												onChange={handleSocialLinkChange}
												placeholder="Twitter URL"
											/>
										</div>
										<div className="social-link-input">
											<span className="social-icon">
												<FaInstagram />
											</span>
											<input
												type="url"
												name="instagram"
												value={formData.socialLinks.instagram}
												onChange={handleSocialLinkChange}
												placeholder="Instagram URL"
											/>
										</div>
										<div className="social-link-input">
											<span className="social-icon">
												<FaLinkedinIn />
											</span>
											<input
												type="url"
												name="linkedin"
												value={formData.socialLinks.linkedin}
												onChange={handleSocialLinkChange}
												placeholder="LinkedIn URL"
											/>
										</div>
									</div>
								</div>

								<div className="form-group">
									<label>Tags</label>
									<div className="tags-container">
										{formData.tags.map((tag, index) => (
											<div className="tag-input" key={`tag-${index}`}>
												<input
													type="text"
													value={tag}
													onChange={(e) => handleTagChange(index, e.target.value)}
													placeholder="Enter tag"
												/>
												<button
													type="button"
													className="remove-button"
													onClick={() => removeTag(index)}
													disabled={formData.tags.length === 1}
												>
													<svg
														xmlns="http://www.w3.org/2000/svg"
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													>
														<line x1="18" y1="6" x2="6" y2="18"></line>
														<line x1="6" y1="6" x2="18" y2="18"></line>
													</svg>
												</button>
											</div>
										))}
										<button type="button" className="add-button" onClick={addTag}>
											Add Tag
										</button>
									</div>
								</div>
								
								{!isEditing && (
									<div className="form-group">
										<label>First Comment</label>
										<textarea
											name="firstComment"
											value={formData.firstComment}
											onChange={handleChange}
											placeholder="If you were to comment about your product, what would you say? Share your thoughts to kickstart the discussion."
											rows="3"
										></textarea>
									</div>
								)}
								
								<div className="form-actions">
									{!isEditing && (
										<button type="button" className="back-button" onClick={goToPrevStep}>
											Back
										</button>
									)}
									<button type="button" className="next-button" onClick={goToNextStep}>
										Next
									</button>
								</div>
							</div>
						)}

						{/* Step 3: Media */}
						{step === 4 && (
							<div className="form-step">
								<h3>Media</h3>
								
								<div className="form-group">
									<label>Logo</label>
									<div className="file-input-container">
										<input
											type="file"
											ref={logoInputRef}
											onChange={(e) => handleFileChange(e, "logo")}
											accept="image/*"
											className="file-input"
										/>
										<button
											type="button"
											className="file-input-button"
											onClick={() => logoInputRef.current.click()}
										>
											{formData.logo ? "Change Logo" : "Upload Logo"}
										</button>
										{formData.logo && (
											<div className="file-preview">
												<span className="file-name">
													{getFileNameFromBase64(formData.logo)}
												</span>
												<img
													src={formData.logo}
													alt="Logo Preview"
													className="image-preview"
												/>
											</div>
										)}
									</div>
								</div>

								<div className="form-group">
									<label>Banner Image (Recommended size: 1200x630)</label>
									<div className="file-input-container">
										<input
											type="file"
											ref={bannerInputRef}
											onChange={(e) => handleFileChange(e, "banner")}
											accept="image/*"
											className="file-input"
										/>
										<button
											type="button"
											className="file-input-button"
											onClick={() => bannerInputRef.current.click()}
										>
											{formData.banner ? "Change Banner" : "Upload Banner"}
										</button>
										{formData.banner && (
											<div className="file-preview">
												<span className="file-name">
													{getFileNameFromBase64(formData.banner)}
												</span>
												<img
													src={formData.banner}
													alt="Banner Preview"
													className="image-preview banner-preview"
												/>
											</div>
										)}
									</div>
								</div>

								<div className="form-group">
									<label>Gallery (1270x760px)</label>
									<div className="gallery-container">
										{formData.productImages.map((image, index) => (
											<div className="gallery-image-input" key={`gallery-${index}`}>
												<div className="file-input-container">
													<input
														type="file"
														ref={(el) => (productImagesInputRefs.current[index] = el)}
														onChange={(e) => handleproductImagesFileChange(e, index)}
														accept="image/*"
														className="file-input"
													/>
													<button
														type="button"
														className="file-input-button"
														onClick={() => productImagesInputRefs.current[index].click()}
													>
														{image ? "Change Image" : "Upload Image"}
													</button>
													{image && (
														<div className="file-preview">
															<span className="file-name">
																{getFileNameFromBase64(image)}
															</span>
															<img
																src={image}
																alt="Gallery Preview"
																className="image-preview gallery-preview"
															/>
														</div>
													)}
												</div>
												<button
													type="button"
													className="remove-button"
													onClick={() => removeproductImagesImage(index)}
													disabled={formData.productImages.length === 1}
												>
													<svg
														xmlns="http://www.w3.org/2000/svg"
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													>
														<line x1="18" y1="6" x2="6" y2="18"></line>
														<line x1="6" y1="6" x2="18" y2="18"></line>
													</svg>
												</button>
											</div>
										))}
										<button type="button" className="add-gallery-button" onClick={addproductImagesImage}>
											<FaPlus className="add-icon" />
											Add Product Image
										</button>
									</div>
								</div>
								
								<div className="form-actions">
									<button type="button" className="back-button" onClick={goToPrevStep}>
										Back
									</button>
									<button type="button" className="next-button" onClick={goToNextStep}>
										Next
									</button>
								</div>
							</div>
						)}

						{/* Step 4: Makers */}
						{step === 5 && (
							<div className="form-step">
								<h3>Makers</h3>
								<p className="helper-text">Add people who worked on this product. They must be CapitalHub users.</p>
								
								<div className="makers-search">
									<input
										type="text"
										value={searchInput}
										onChange={handleSearchInputChange}
										placeholder="Search for CapitalHub users"
										className="maker-search-input"
									/>
									
									{searchInput.trim() !== "" && (
										<div className="search-results">
											{searchLoading ? (
												<div className="loading-spinner">
													<div className="spinner-border text-secondary" role="status">
														<span className="visually-hidden">Loading...</span>
													</div>
												</div>
											) : searchResults.length > 0 ? (
												<div className="user-results">
													{searchResults.map(user => (
														<div 
															key={user._id} 
															className="user-result-item"
															onClick={() => addMaker(user)}
														>
															<img 
																src={user.profilePicture || "https://via.placeholder.com/50"} 
																alt={`${user.firstName} ${user.lastName}`} 
																className="user-avatar"
															/>
															<div className="user-info">
																<span className="user-name">{user.firstName} {user.lastName}</span>
																<span className="user-username">@{user.userName}</span>
															</div>
														</div>
													))}
												</div>
											) : (
												<p className="no-results">No users found</p>
											)}
										</div>
									)}
								</div>
								
								<div className="selected-makers">
									{formData.makers.length === 0 ? (
										<p className="no-makers">No makers added yet</p>
									) : (
										<div className="makers-list">
											{formData.makers.map(maker => (
												<div key={maker._id} className="maker-item">
													<div className="maker-info">
														<img 
															src={maker.profilePicture || "https://via.placeholder.com/50"} 
															alt={`${maker.firstName} ${maker.lastName}`} 
															className="maker-avatar"
														/>
														<span className="maker-name">{maker.firstName} {maker.lastName}</span>
													</div>
													<button 
														type="button" 
														className="remove-maker-btn"
														onClick={() => removeMaker(maker._id)}
													>
														<svg
															xmlns="http://www.w3.org/2000/svg"
															viewBox="0 0 24 24"
															fill="none"
															stroke="currentColor"
															strokeWidth="2"
															strokeLinecap="round"
															strokeLinejoin="round"
														>
															<line x1="18" y1="6" x2="6" y2="18"></line>
															<line x1="6" y1="6" x2="18" y2="18"></line>
														</svg>
													</button>
												</div>
											))}
										</div>
									)}
								</div>
								
								<div className="form-actions">
									<button type="button" className="back-button" onClick={goToPrevStep}>
										Back
									</button>
									<button type="button" className="next-button" onClick={goToNextStep}>
										Next
									</button>
								</div>
							</div>
						)}

						{/* Step 5: Extras - Only show in non-editing mode */}
						{step === 6 && !isEditing && (
							<div className="form-step">
								<h3>Extras</h3>
								
								<div className="form-group">
									<label>Pricing</label>
									<div className="pricing-options">
										<div className="pricing-option">
											<input
												type="radio"
												id="free"
												name="pricing"
												value="Free"
												checked={formData.pricing === "Free"}
												onChange={handleChange}
											/>
											<label htmlFor="free">Free</label>
										</div>
										<div className="pricing-option">
											<input
												type="radio"
												id="paid"
												name="pricing"
												value="Paid"
												checked={formData.pricing === "Paid"}
												onChange={handleChange}
											/>
											<label htmlFor="paid">Paid</label>
										</div>
										<div className="pricing-option">
											<input
												type="radio"
												id="trial"
												name="pricing"
												value="Trial"
												checked={formData.pricing === "Trial"}
												onChange={handleChange}
											/>
											<label htmlFor="trial">Trial Available</label>
										</div>
									</div>
									
									{(formData.pricing === "Paid" || formData.pricing === "Trial") && (
										<div className="price-input-container">
											<div className="price-input-group">
												<span className="currency-symbol">₹</span>
												<input
													type="number"
													name="priceAmount"
													value={formData.priceAmount}
													onChange={handleChange}
													placeholder="0"
													min="0"
													step="1"
												/>
											</div>
											<select
												name="priceType"
												value={formData.priceType}
												onChange={handleChange}
												className="price-type-select"
											>
												<option value="One-time">One-time</option>
												<option value="Monthly">Monthly</option>
												<option value="Yearly">Yearly</option>
											</select>
										</div>
									)}
								</div>

								<div className="form-group">
									<label>Promo Code (For subscribers only)</label>
									<input
										type="text"
										name="promoCode"
										value={formData.promoCode}
										onChange={handleChange}
										placeholder="Enter promo code if available"
									/>
								</div>
								
								<div className="form-actions">
									<button type="button" className="back-button" onClick={goToPrevStep}>
										Back
									</button>
									<button type="button" className="next-button" onClick={goToNextStep}>
										Next
									</button>
								</div>
							</div>
						)}

						{/* Step 6: Funding Stage */}
						{step === 7 && !isEditing && (
							<div className="form-step">
								<h3>Funding Stage</h3>
								<p className="helper-text">Select the current stage of your startup</p>
								
								<div className="stage-cards-container">
									<div 
										className={`stage-card ${formData.stage === "Idea" ? "selected" : ""}`}
										onClick={() => handleStageSelect("Idea")}
									>
										<div className="stage-icon">💡</div>
										<h4>Idea Stage</h4>
										<p className="stage-description">
											Just starting out with a brilliant concept. Your product is in the ideation phase, and you're looking to validate your idea and gather initial feedback.
										</p>
										{/* <p className="stage-sub-description">
											Perfect for entrepreneurs who are ready to transform their vision into reality.
										</p> */}
									</div>
									
									<div 
										className={`stage-card ${formData.stage === "Early" ? "selected" : ""}`}
										onClick={() => handleStageSelect("Early")}
									>
										<div className="stage-icon">🚀</div>
										<h4>Early Stage</h4>
										<p className="stage-description">
											You have an MVP or prototype ready. Your product is being tested with early users, and you're gathering valuable market feedback.
										</p>
										{/* <p className="stage-sub-description">
											Ideal for startups with a working product seeking initial investment and market validation.
										</p> */}
									</div>
									
									<div 
										className={`stage-card ${formData.stage === "Growth" ? "selected" : ""}`}
										onClick={() => handleStageSelect("Growth")}
									>
										<div className="stage-icon">📈</div>
										<h4>Growth Stage</h4>
										<p className="stage-description">
											Your product has proven market fit and is generating revenue. You're ready to scale operations and expand your market presence.
										</p>
										{/* <p className="stage-sub-description">
											For established startups looking to accelerate growth and capture larger market share.
										</p> */}
									</div>
								</div>

								{/* KYC and Pitch Deck section for Early and Growth stages */}
								{(formData.stage === "Early" || formData.stage === "Growth") && (
									<div className="stage-details-section">
										<div className="stage-details-content">
											{/* KYC Documents Section */}
											{!user?.kycDocuments?.aadharCard && !user?.kycDocuments?.panCard ? (
												<div className="form-group kyc-section">
													<h4>KYC Documents</h4>
													<p className="helper-text">Please upload your KYC documents for verification (mandatory).</p>
													
													<div className="kyc-upload-container">
														<div className="kyc-upload-item">
															<label>Aadhar Card *</label>
															<input
																type="file"
																accept=".pdf,.jpg,.jpeg,.png"
																onChange={(e) => handleKYCFileChange(e, 'aadharCard')}
																className={formErrors.aadharCard ? 'error' : ''}
															/>
															{formErrors.aadharCard && (
																<span className="error-message">{formErrors.aadharCard}</span>
															)}
														</div>
														
														<div className="kyc-upload-item">
															<label>PAN Card *</label>
															<input
																type="file"
																accept=".pdf,.jpg,.jpeg,.png"
																onChange={(e) => handleKYCFileChange(e, 'panCard')}
																className={formErrors.panCard ? 'error' : ''}
															/>
															{formErrors.panCard && (
																<span className="error-message">{formErrors.panCard}</span>
															)}
														</div>
													</div>
												</div>
											) : (
												<div className="form-group kyc-verified">
													<h4>KYC Status</h4>
													<p className="success-message">✓ KYC documents already verified</p>
												</div>
											)}

											{/* Pitch Deck Section */}
											<div className="form-group pitch-deck-section">
												<h4>Pitch Deck</h4>
												<p className="helper-text">Please either upload your pitch deck or request a review (mandatory).</p>
												<div className="pitch-deck-container">
													<div className="pitch-deck-upload">
														<label>Upload Pitch Deck</label>
														<input
															type="file"
															accept=".pdf,.ppt,.pptx"
															onChange={handlePitchDeckFileChange}
															className={formErrors.pitchDeck ? 'error' : ''}
														/>
													</div>
													
													<div className="pitch-deck-request">
														<label className="switch-label">
															<input
																type="checkbox"
																checked={formData.pitchDeck.requestedFromAnalyst}
																onChange={handlePitchDeckRequestChange}
															/>
															<span className="switch-slider"></span>
															Help me build my pitchdeck (If you don't have a pitchdeck, tick the button to get help from our team.)
														</label>
													</div>
													{formErrors.pitchDeck && (
														<span className="error-message">{formErrors.pitchDeck}</span>
													)}
												</div>
											</div>
										</div>
									</div>
								)}
								
								<div className="form-actions">
									<button type="button" className="back-button" onClick={goToPrevStep}>
										Back
									</button>
									<button type="submit" className="submit-button">
										Submit Product
									</button>
								</div>
							</div>
						)}
					</form>
				</div>
			</div>
		</div>
	);
};

export default CreateProductPopup;